// src/pages/dashboard/director/DirectorProfilePage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Form, Row, Col, Button, Alert, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

import MetaTags from '../../../components/MetaTags';
import { useAuth } from '../../../context/AuthContext';
import api, { fetchCdDirJogetProfileApi } from '../../../services/api';
import { fetchLookupDataApi } from '../../../services/lookupApi';
// import './DirectorProfilePage.css'; // Keep if you have specific page styles

// Components
import DirectorProfileWizard from '../../../components/dashboard/directorProfile/DirectorProfileWizard';
import StepDirectorCompanyInfo from '../../../components/dashboard/directorProfile/StepDirectorCompanyInfo';
import StepDirectorAddress from '../../../components/dashboard/directorProfile/StepDirectorAddress';
import StepDirectorTeamMembers from '../../../components/dashboard/directorProfile/StepDirectorTeamMembers';
import StepDirectorSocialMedia from '../../../components/dashboard/directorProfile/StepDirectorSocialMedia';

// Utils & Schema
import { agencyProfileSchema } from '../../../validation/agencyProfileSchema'; // Use the renamed schema
import { CACHE_KEY_AGENCY_LOOKUPS, LOOKUP_MAPPING, WIZARD_STEPS_AGENCY_PROFILE } from '../../../utils/agencyProfileConstants'; // Use renamed constants
import { apiToDateObject, dateObjectToApiFormat, transformToSelectOptions } from '../../../utils/agencyProfileHelpers'; // Use renamed helpers
import { getNestedError } from '../../../utils/formUtils'; // Import generic getNestedError from formUtils
import CryptoJS from "crypto-js";

const DirectorProfilePage = () => {
    const { user } = useAuth();
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [apiMessage, setApiMessage] = useState({ type: '', text: '' });
    const [profileImagePreview, setProfileImagePreview] = useState(null);
    const [currentJogetRecordId, setCurrentJogetRecordId] = useState(null);
    const [isWhatsappSame, setIsWhatsappSame] = useState(false);

    const [lookupOptions, setLookupOptions] = useState({
        countryOfResidence: [], specialization: [], addressType: [],
        pocDesignation: [], pocContactType: []
    });

    const { control, handleSubmit, reset, trigger, setValue, watch, clearErrors, formState: { errors } } = useForm({
        resolver: yupResolver(agencyProfileSchema), // Use the renamed schema
        mode: 'onChange',
        defaultValues: { 
            companyIncorporationDate: null, 
            addressType: 'Primary',
            teamMembers: [{ name: '', email: '', mobile: '', designation: '', contactType: '' }] 
        }
    });

    // --- Pan/Aadhar Conditional Validation Logic ---
    const [firstFilled, setFirstFilled] = useState(null);
    const pan = watch("panNumber");
    const aadhar = watch("aadharNumber");

    useEffect(() => {
        if (pan || aadhar) { clearErrors(["panNumber", "aadharNumber"]); }
    }, [pan, aadhar, clearErrors]);

    useEffect(() => {
        trigger(["panNumber", "aadharNumber"]);
    }, [pan, aadhar, trigger]);

    useEffect(() => {
        if (!firstFilled) {
            if (pan) setFirstFilled("pan");
            else if (aadhar) setFirstFilled("aadhar");
        }
    }, [pan, aadhar, firstFilled]);

    useEffect(() => {
        if (!pan && !aadhar) { setFirstFilled(null); }
    }, [pan, aadhar]);

    const isPanRequired = (!pan && !aadhar) || firstFilled === "pan";
    const isAadharRequired = (!pan && !aadhar) || firstFilled === "aadhar";
    // --- End Pan/Aadhar Logic ---

    const watchedMobile = watch('mobileNumber');

    // WhatsApp Logic
    useEffect(() => {
        if (isWhatsappSame) setValue('whatsappNo', watchedMobile || '');
    }, [isWhatsappSame, watchedMobile, setValue]);

    const handleWhatsappCheck = (e) => {
        setIsWhatsappSame(e.target.checked);
        if (e.target.checked) setValue('whatsappNo', watchedMobile || '');
        else setValue('whatsappNo', '');
    };

    const SECRET = process.env.REACT_APP_CRYPTO_SECRET;

    const encryptLocal = (data) => {
    return CryptoJS.AES.encrypt(
        JSON.stringify(data),
        SECRET
    ).toString();
    };

    const decryptLocal = (data) => {
    const bytes = CryptoJS.AES.decrypt(data, SECRET);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    };

    // Pincode Logic
    const handlePincodeLookup = useCallback(async (pincode) => {
        if (!pincode || pincode.length !== 6) return;
        try {
            const response = await axios.get(`https://api.postalpincode.in/pincode/${pincode}`);
            if (response.data?.[0]?.Status === 'Success') {
                const { State, District } = response.data[0].PostOffice[0];
                setValue('state', State, { shouldValidate: true });
                setValue('district', District, { shouldValidate: true });
            } else setApiMessage({ type: 'warning', text: `Pincode not found.` });
        } catch (error) { console.error("Pincode API Error"); }
    }, [setValue]);
    
    // Load data and lookups
    const loadData = useCallback(async () => {
        if (!user?.id) return;
        setPageLoading(true);
        try {
            // Lookups
            let cachedLookups = localStorage.getItem(CACHE_KEY_AGENCY_LOOKUPS);
            let finalLookups = {};
            if (cachedLookups) {
                finalLookups = JSON.parse(cachedLookups);
            } else {
                const groups = LOOKUP_MAPPING;
                const promises = Object.entries(groups).map(([key, group]) => fetchLookupDataApi(group));
                const results = await Promise.all(promises);
                Object.keys(groups).forEach((key, i) => finalLookups[key] = transformToSelectOptions(results[i].data.data));
                // localStorage.setItem(CACHE_KEY_AGENCY_LOOKUPS, JSON.stringify(finalLookups));
                localStorage.setItem(CACHE_KEY_AGENCY_LOOKUPS, encryptLocal(finalLookups));
                
            }
            setLookupOptions(finalLookups);

            // Profile Data (Assuming a different endpoint for Director vs. CD)
            // *** IMPORTANT: Adjust this API endpoint if it's different for Directors ***
            const response = await fetchCdDirJogetProfileApi(user.id); // <--- ASSUMED ENDPOINT
            const jogetData = response.data.jogetData;

            if (jogetData) {
                setCurrentJogetRecordId(jogetData.id || jogetData.pk_id);
                setProfileImagePreview(jogetData.profileImage || null);
                
                const specs = (jogetData.specialization || '').split(',').map(s => s.trim())
                    .map(val => finalLookups.specialization.find(o => o.value === val)).filter(Boolean);

                let teamData = [{ name: '', email: '', mobile: '', designation: '', contactType: '' }];
                if (jogetData.teamMembersJson) {
                    try { teamData = JSON.parse(jogetData.teamMembersJson); } catch (e) {}
                } else if (jogetData.poc_PersonName) { // Fallback for old data structure
                    teamData = [{ 
                        name: jogetData.poc_PersonName || '', 
                        email: jogetData.poc_email || '', 
                        mobile: jogetData.poc_mobileNumber || '', 
                        designation: jogetData.poc_designation || '', 
                        contactType: jogetData.poc_contactType || '' 
                    }];
                }

                reset({
                    ...jogetData,
                    yourName: jogetData.yourName || user.full_name || '',
                    email: jogetData.email || user.email || '',
                    mobileNumber: jogetData.mobileNumber || user.mobile_no || '',
                    alternateMobileNumber: jogetData.alternateMobileNumber || '',
                    specialization: specs,
                    companyIncorporationDate: apiToDateObject(jogetData.companyIncorporationDate),
                    website: jogetData.website || '',
                    addressType: 'Primary', 
                    addressLine2: jogetData.addressLine2 || '',
                    whatsappNo: jogetData.whatsappNo || '',
                    gstNumber: jogetData.gstNumber || '',
                    teamMembers: teamData.length > 0 ? teamData : [{ name: '', email: '', mobile: '', designation: '', contactType: '' }]
                });

                if (jogetData.whatsappNo && jogetData.whatsappNo === jogetData.mobileNumber) setIsWhatsappSame(true);
            } else {
                // Default values for new profile
                reset({ 
                    yourName: user.full_name, 
                    email: user.email, 
                    mobileNumber: user.mobile_no, 
                    addressType: 'Primary', 
                    teamMembers: [{ name: '', email: '', mobile: '', designation: '', contactType: '' }] 
                });
            }
        } catch (error) { 
            console.error("Profile Load Error:", error);
            setApiMessage({ type: 'danger', text: 'Failed to load profile.' }); 
        } finally { setPageLoading(false); }
    }, [user, reset]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleNext = async () => {
        const isValid = await trigger(WIZARD_STEPS_AGENCY_PROFILE[currentStep].fields); // Use renamed WIZARD_STEPS
        if (isValid) {
            setApiMessage({ type: '', text: '' });
            if (currentStep < WIZARD_STEPS_AGENCY_PROFILE.length - 1) { setCurrentStep(c => c + 1); window.scrollTo(0,0); }
            else handleSubmit(onSubmit)();
        } else {
            setApiMessage({ type: 'danger', text: 'Please fill all required fields.' });
            window.scrollTo(0,0);
        }
    };

    const handleBack = () => { if (currentStep > 0) setCurrentStep(c => c - 1); window.scrollTo(0,0); };

    // --- UPDATED SUBMIT LOGIC ---
    const onSubmit = async (data) => {
        setLoading(true);
        try {
            // Prepare Data
            const formattedDate = data.companyIncorporationDate ? dateObjectToApiFormat(data.companyIncorporationDate) : '';
            const specializationCSV = (data.specialization || []).map(s => s.value).join(',');
            const teamJsonString = JSON.stringify(data.teamMembers);

            // Base Payload
            const basePayload = {
                id: currentJogetRecordId || '',
                yourName: data.yourName,
                email: data.email,
                mobileNumber: data.mobileNumber,
                alternateMobileNumber: data.alternateMobileNumber || '',
                agencyName: data.agencyName,
                website: data.website || '',
                aboutAgency: data.aboutAgency,
                countryOfResidence: data.countryOfResidence,
                companyIncorporationDate: formattedDate,
                specialization: specializationCSV,
                
                addressType: data.addressType,
                pinCode: data.pinCode,
                addressLine1: data.addressLine1,
                addressLine2: data.addressLine2 || '',
                state: data.state,
                district: data.district,
                panNumber: data.panNumber,
                aadharNumber: data.aadharNumber,
                gstNumber: data.gstNumber || '',

                facebookProfileUrl: data.facebookProfileUrl || '',
                instagramProfileUrl: data.instagramProfileUrl || '',
                linkedinProfileUrl: data.linkedinProfileUrl || '',
                twitterProfileUrl: data.twitterProfileUrl || '',
                youtubeUrl: data.youtubeUrl || '',
                whatsappNo: data.whatsappNo || '',

                teamMembersJson: teamJsonString,
                user_id: user.id // Pass user ID explicitly
            };

            // *** IMPORTANT: Adjust this API endpoint for Director if different ***
            const endpoint = data.profileImageFile instanceof File ? '/cddir/profile/cd-dir/withfile' : '/cddir/profile/cd-dir'; // <--- ASSUMED ENDPOINTS

            if (data.profileImageFile instanceof File) {
                // Multipart Upload
                const formData = new FormData();
                for (const key in basePayload) {
                    formData.append(key, basePayload[key]);
                }
                formData.append('profileImageFile', data.profileImageFile);

                const { data: res } = await api.post(endpoint, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                handleSuccess(res);

            } else {
                // JSON Upload
                const { data: res } = await api.post(endpoint, basePayload);
                handleSuccess(res);
            }
        } catch (error) {
            console.error("Submit Error:", error);
            setApiMessage({ type: 'danger', text: error.response?.data?.message || 'Failed to save profile.' });
        } finally { setLoading(false); }
    };

    const handleSuccess = (res) => {
        setApiMessage({ type: 'success', text: 'Profile Saved Successfully!' });
        if (res.jogetResponse?.id) setCurrentJogetRecordId(res.jogetResponse.id);
    };

    const handleImageChange = (e) => {
        if (e.target.files[0]) {
            setValue('profileImageFile', e.target.files[0]);
            setProfileImagePreview(URL.createObjectURL(e.target.files[0]));
        }
    };
    
    if (pageLoading) return <div className="text-center p-5"><Spinner animation="border" variant="danger" /></div>;

    return (
        <div className="director-profile-page mt-10">
            <MetaTags title="Director Profile" />
            <h5 className="text-uppercase text-white fw-bold mb-4"><span className="text-danger border-start border-4 border-danger ps-3">Director Profile</span></h5>

            <DirectorProfileWizard currentStep={currentStep} />

            {apiMessage.text && <Alert variant={apiMessage.type} dismissible onClose={() => setApiMessage({type:'', text:''})}>{apiMessage.text}</Alert>}

            <div className="custom-card-box">
                <Form noValidate>
                    {currentStep === 0 && (
                        <StepDirectorCompanyInfo 
                            control={control} errors={errors} lookupOptions={lookupOptions} 
                            profileImagePreview={profileImagePreview} handleImageChange={handleImageChange} 
                            isWhatsappSame={isWhatsappSame} handleWhatsappCheck={handleWhatsappCheck}
                            isPanRequired={isPanRequired} isAadharRequired={isAadharRequired}
                        />
                    )}

                    {currentStep === 1 && (
                        <StepDirectorAddress 
                            control={control} errors={errors} 
                            handlePincodeLookup={handlePincodeLookup} 
                        />
                    )}

                    {currentStep === 2 && (
                        <StepDirectorTeamMembers 
                            control={control} errors={errors} lookupOptions={lookupOptions} 
                        />
                    )}

                    {currentStep === 3 && (
                        <StepDirectorSocialMedia 
                            control={control} errors={errors} 
                            isWhatsappSame={isWhatsappSame} 
                        />
                    )}
                </Form>
            </div>

            <div className="d-flex justify-content-between mt-4">
                <Button variant="outline-secondary" onClick={handleBack} disabled={currentStep === 0 || loading} size="lg" className="px-4 border-secondary text-light"><FaChevronLeft className="me-2" /> Back</Button>
                <Button variant="danger" onClick={handleNext} disabled={loading} size="lg" className="px-5 fw-bold shadow-lg" style={{ minWidth: '180px' }}>{loading ? <Spinner animation="border" size="sm" className="text-white"/> : (currentStep === 3 ? 'SAVE PROFILE' : <>Next <FaChevronRight className="ms-2" /></>)}</Button>
            </div>
        </div>
    );
};
export default DirectorProfilePage;